import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const leexiPricingArticle: BlogArticle = {
  key: BlogArticleKey.LEEXI_PRICING,
  slug: BlogSlugs.LEEXI_PRICING,
  categoryId: BlogCategory.AI_TOOLS,
  date: "2026-08-29",
  updated: "2026-09-29",
  readTime: 8,
  locales: {
    fr: {
      title: "Leexi : prix, fonctionnalités et limites réelles",
      excerpt:
        "Les plans, ce qu'ils contiennent vraiment, et pourquoi il faut se méfier de la moitié des critiques qu'on lit sur Leexi. Un point factuel avant de signer.",
      description:
        "Combien coûte Leexi, que contient chaque plan, et quelles sont ses limites réelles ? Un point factuel sur les tarifs et les fonctionnalités.",
      ctaTitle: "Une fois l'appel analysé, il reste le plus dur",
      ctaText:
        "Voyez ce que devient une objection quand elle rejoint la fiche du client et se compte d'un échange à l'autre, au lieu de rester dans un compte rendu. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.LEAD,
          text: "Chercher un avis sur Leexi, c'est tomber sur des pages écrites par ses concurrents. Voici ce qui est vérifiable, et ce qui ne l'est pas.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Leexi est un outil d'analyse conversationnelle : il rejoint vos réunions, transcrit, résume et remonte les sujets abordés. Positionnement européen, prise en main rapide, et une intégration CRM sur les plans hauts. C'est un concurrent direct de Modjo, sur un ticket d'entrée plus bas.",
        },
        { type: ArticleBlockType.H2, text: "Les prix, plan par plan" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Contrairement à Modjo, Leexi publie une grille. Les tarifs sont dégressifs selon la taille de l'équipe, ce qui rend les montants ci-dessous indicatifs plutôt qu'exacts.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Plan", "Prix et contenu"],
          rows: [
            [
              "Starter",
              "Autour de 15 $ par mois. Environ 20 heures de réunion, accès réduit aux fonctions principales.",
            ],
            [
              "AI Meeting",
              "Autour de 29 $ par mois. Comptes rendus automatiques sur vos réunions et vos appels.",
            ],
            [
              "Business",
              "Autour de 39 € par utilisateur et par mois. Remontée automatique dans le CRM, connexion VOIP, résumés sur mesure.",
            ],
            [
              "Enterprise",
              "Sur devis. Les fonctions analytiques pour managers sont réservées à ce niveau.",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le point le plus structurant n'est pas dans ce tableau : **Leexi n'a pas d'offre gratuite**. Si votre besoin est de trois comptes rendus par mois, ce n'est pas le bon outil, et un notetaker gratuit fera le travail.",
        },
        { type: ArticleBlockType.H2, text: "Les limites, et d'où viennent les critiques" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Il faut être méthodique ici, parce que c'est là que la plupart des articles dérapent. Les pages qui listent les faiblesses de Leexi sont souvent publiées par tl;dv ou par des solutions de transcription concurrentes. Ce ne sont pas des sources neutres, et leurs griefs méritent d'être lus comme des arguments commerciaux.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Deux exemples de contradiction directe entre sources : certaines vantent une **approche multilingue solide**, d'autres reprochent une **prise en charge limitée hors anglais**. Les deux ne peuvent pas être vraies. Testez sur votre propre langue de travail avant de conclure.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Vérifiable, car dans la grille officielle** : pas d'offre gratuite, et les fonctions analytiques réservées aux plans hauts.",
            "**Rapporté, à vérifier vous-même** : des quotas sur la fonction de questions posées aux appels, et des intégrations moins nombreuses que chez les gros acteurs.",
            "**Contesté entre sources** : la qualité du multilingue. À tester, pas à croire.",
          ],
        },
        { type: ArticleBlockType.H2, text: "Pour qui Leexi est le bon choix" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si vous voulez la famille d'outil de Modjo sans son plancher de sièges ni son devis obligatoire, Leexi est l'option la plus directe. La grille publiée vous permet de décider seul, ce qui est déjà beaucoup.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si votre besoin réel est de garder une trace de vos réunions, vous payez pour de l'analyse que vous n'utiliserez pas. Fathom ou tl;dv suffisent, et ils ont une offre gratuite.",
        },
        { type: ArticleBlockType.H2, text: "La limite que Leexi partage avec tous les autres" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Elle n'a rien à voir avec le prix, et aucun de ses concurrents ne la corrige non plus. Ces outils répondent à *qu'est-ce qui s'est dit ?* et s'arrêtent là. L'objection détectée dans onze appels reste dans l'outil, sans rejoindre la fiche du client ni se compter d'un client à l'autre.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Ce que fait un outil d'analyse",
            items: [
              "Transcrire et résumer chaque appel",
              "Repérer les sujets et les objections",
              "Remonter un résumé dans le CRM",
              "S'arrêter là",
            ],
          },
          good: {
            title: "Ce qu'il reste à faire ensuite",
            items: [
              "Rattacher chaque échange au bon client",
              "Suivre ce qui a été promis, et pour quand",
              "Compter ce qui revient d'un client à l'autre",
              "Retrouver le passage exact quand on en a besoin",
            ],
          },
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "C'est la colonne de droite que Meidly prend en charge",
          text: "Meidly ne remplace pas Leexi et ne cherche pas à le faire. Chaque échange rejoint la fiche de son client : synthèse à jour, engagements avec leur échéance, deals détectés, passages clés tagués. L'assistant compte les thèmes qui reviennent et cite chaque passage. Les deux peuvent coexister : l'un regarde l'appel, l'autre exploite ce qu'il contient.",
          linkLabel: "Voir les fiches clients",
          linkPath: PublicPaths.HOME,
        },
      ],
      faq: [
        {
          question: "Combien coûte Leexi ?",
          answer:
            "Leexi publie une grille avec plusieurs niveaux : un plan Starter autour de 15 $ par mois limité en heures de réunion, un plan AI Meeting autour de 29 $, un plan Business autour de 39 € par utilisateur et par mois avec la remontée CRM, et un plan Enterprise sur devis. Les tarifs sont dégressifs selon la taille de l'équipe, vérifiez chez l'éditeur pour votre cas.",
        },
        {
          question: "Leexi a-t-il une version gratuite ?",
          answer:
            "Non. C'est sa différence la plus concrète avec Fathom ou tl;dv, qui proposent tous deux un palier gratuit. Si vous voulez tester sans payer, commencez par un notetaker gratuit et passez à Leexi si l'analyse vous manque vraiment.",
        },
        {
          question: "Leexi ou Modjo, lequel choisir ?",
          answer:
            "Modjo vise les équipes structurées avec un manager qui coache, sur devis et avec un plancher de sièges. Leexi couvre une bonne partie du même besoin avec une grille publique et sans passer par un commercial. Pour une petite équipe, Leexi est plus accessible. Pour du coaching d'équipe outillé, Modjo reste plus complet.",
        },
        {
          question: "Peut-on faire confiance aux avis négatifs sur Leexi ?",
          answer:
            "Avec prudence. Beaucoup de pages qui listent ses faiblesses sont publiées par des concurrents directs, notamment des outils de transcription. Certaines critiques se contredisent d'une source à l'autre, en particulier sur la qualité du multilingue. Traitez ces griefs comme des pistes à vérifier pendant un essai, pas comme des faits.",
        },
      ],
    },
    en: {
      title: "Leexi: pricing, features and real limits",
      excerpt:
        "The plans, what they actually contain, and why half of the criticism you read about Leexi deserves a second look. A factual review before you sign.",
      description:
        "How much does Leexi cost, what is in each plan, and what are its real limits? A factual look at its pricing and its features.",
      ctaTitle: "Once the call is analysed, the hard part remains",
      ctaText:
        "See what an objection becomes when it lands in the customer's file and gets counted across conversations, instead of sitting in a summary. No commitment.",
      body: [
        {
          type: ArticleBlockType.LEAD,
          text: "Looking for a Leexi review means landing on pages written by its competitors. Here is what is verifiable, and what is not.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Leexi is a conversation analysis tool: it joins your meetings, transcribes, summarises and surfaces the topics discussed. European positioning, quick onboarding, and CRM integration on the higher plans. It is a direct Modjo competitor at a lower entry ticket.",
        },
        { type: ArticleBlockType.H2, text: "Pricing, plan by plan" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Unlike Modjo, Leexi publishes a grid. Prices scale down with team size, which makes the figures below indicative rather than exact.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Plan", "Price and contents"],
          rows: [
            [
              "Starter",
              "Around 15 dollars a month. Roughly 20 hours of meetings, reduced access to the main features.",
            ],
            [
              "AI Meeting",
              "Around 29 dollars a month. Automatic summaries on your meetings and calls.",
            ],
            [
              "Business",
              "Around 39 euros per user per month. Automatic CRM push, VOIP connection, custom summaries.",
            ],
            ["Enterprise", "Quote based. Manager analytics are reserved for this tier."],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The most structural point is not in that table: **Leexi has no free plan**. If your need is three summaries a month, this is not the right tool, and a free notetaker will do the job.",
        },
        { type: ArticleBlockType.H2, text: "The limits, and where the criticism comes from" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This needs care, because it is where most articles go wrong. The pages listing Leexi weaknesses are often published by tl;dv or by competing transcription products. Those are not neutral sources, and their complaints should be read as sales arguments.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Two sources contradict each other directly: some praise a **solid multilingual approach**, others complain about **limited support outside English**. Both cannot be true. Test it in your own working language before concluding.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Verifiable, because it is in the official grid**: no free plan, and analytics reserved for the higher tiers.",
            "**Reported, verify it yourself**: quotas on the question answering feature, and fewer integrations than the larger players.",
            "**Disputed between sources**: multilingual quality. To be tested, not believed.",
          ],
        },
        { type: ArticleBlockType.H2, text: "Who Leexi is right for" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If you want the Modjo family of tool without its seat floor and its mandatory quote, Leexi is the most direct option. The published grid lets you decide on your own, which already counts for a lot.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If your real need is keeping a record of your meetings, you are paying for analysis you will not use. Fathom or tl;dv are enough, and they have a free plan.",
        },
        { type: ArticleBlockType.H2, text: "The limit Leexi shares with every other tool" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "It has nothing to do with price, and none of its competitors fix it either. These tools answer *what was said?* and stop there. The objection found in eleven calls stays inside the tool, without reaching the customer's file or being counted from one customer to the next.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "What an analysis tool does",
            items: [
              "Transcribe and summarise every call",
              "Spot topics and objections",
              "Push a summary into the CRM",
              "Stop there",
            ],
          },
          good: {
            title: "What is left to do next",
            items: [
              "Attach every conversation to the right customer",
              "Track what was promised, and by when",
              "Count what comes back from one customer to the next",
              "Find the exact passage when you need it",
            ],
          },
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "The right column is what Meidly handles",
          text: "Meidly does not replace Leexi and does not try to. Every conversation lands in its customer's file: an up to date synthesis, commitments with due dates, detected deals, tagged key passages. The assistant counts recurring themes and cites every passage. The two can coexist: one looks at the call, the other uses what it contains.",
          linkLabel: "See the customer files",
          linkPath: PublicPaths.HOME,
        },
      ],
      faq: [
        {
          question: "How much does Leexi cost?",
          answer:
            "Leexi publishes a grid with several tiers: a Starter plan around 15 dollars a month limited in meeting hours, an AI Meeting plan around 29 dollars, a Business plan around 39 euros per user per month with CRM push, and an Enterprise plan on quote. Prices scale down with team size, so check with the vendor for your case.",
        },
        {
          question: "Does Leexi have a free version?",
          answer:
            "No. That is its most concrete difference with Fathom or tl;dv, which both offer a free tier. If you want to test without paying, start with a free notetaker and move to Leexi if you genuinely miss the analysis.",
        },
        {
          question: "Leexi or Modjo, which one should you choose?",
          answer:
            "Modjo targets structured teams with a manager who coaches, quote based and with a seat floor. Leexi covers a good part of the same need with a public grid and without going through a sales rep. For a small team, Leexi is more accessible. For tooled team coaching, Modjo remains more complete.",
        },
        {
          question: "Can you trust the negative reviews about Leexi?",
          answer:
            "With caution. Many pages listing its weaknesses are published by direct competitors, notably transcription tools. Some criticisms contradict each other across sources, especially on multilingual quality. Treat those complaints as things to check during a trial, not as facts.",
        },
      ],
    },
  },
};
